import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';

/**
 * Makes every ngx-bootstrap modal draggable by its header, application-wide.
 *
 * Implemented as a single document-level capturing pointer listener rather than a
 * `.modal-dialog` directive because Angular standalone directives are template-scoped
 * (a class-selector directive would only match in components that import it), whereas
 * this needs to apply by default to all ~197 modal templates without touching them.
 *
 * Behavior (agreed design):
 *  - Drag handle is the `.modal-header` only, so form fields/buttons keep working.
 *  - Position resets every time the modal reopens (predictable, not remembered).
 *  - The dialog is clamped to the viewport so it can't be dragged off-screen.
 *  - Works with static-backdrop modals.
 *
 * Instantiated from RootComponent (the single root shared by every client build).
 */
@Injectable({ providedIn: 'root' })
export class DraggableModalService implements OnDestroy {
    private readonly document = inject(DOCUMENT);
    private readonly zone = inject(NgZone);

    private dialog: HTMLElement | null = null;
    private startX = 0;
    private startY = 0;
    private originX = 0;
    private originY = 0;
    private dragging = false;

    init(): void {
        this.injectCursorStyle();
        // Outside Angular: pointer move/up fire rapidly and never touch bindings.
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('pointerdown', this.onPointerDown, true);
        });
    }

    ngOnDestroy(): void {
        this.document.removeEventListener('pointerdown', this.onPointerDown, true);
        this.document.removeEventListener('pointermove', this.onPointerMove, true);
        this.document.removeEventListener('pointerup', this.onPointerUp, true);
    }

    private readonly onPointerDown = (e: PointerEvent): void => {
        if (e.button !== 0) {
            return;
        }

        const target = e.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const header = target.closest('.modal-header') as HTMLElement | null;
        if (!header) {
            return;
        }

        // Don't hijack interactive controls in the header (close icon, buttons, links, inputs).
        if (target.closest('button, a, input, select, textarea, .close, [data-dismiss], [aria-label="Close"]')) {
            return;
        }

        const dialog = header.closest('.modal-dialog') as HTMLElement | null;
        if (!dialog) {
            return;
        }

        this.dialog = dialog;
        this.dragging = true;
        this.startX = e.clientX;
        this.startY = e.clientY;

        const current = this.readTranslate(dialog);
        this.originX = current.x;
        this.originY = current.y;

        // ngx-bootstrap reuses the same .modal-dialog element across opens, so clear the
        // dragged position when the modal closes => it always reopens centered.
        this.observeClose(dialog);

        dialog.style.willChange = 'transform';
        this.document.body.style.userSelect = 'none';

        this.document.addEventListener('pointermove', this.onPointerMove, true);
        this.document.addEventListener('pointerup', this.onPointerUp, true);
    };

    private readonly onPointerMove = (e: PointerEvent): void => {
        if (!this.dragging || !this.dialog) {
            return;
        }

        const win = this.document.defaultView;
        if (!win) {
            return;
        }

        let nextX = this.originX + (e.clientX - this.startX);
        let nextY = this.originY + (e.clientY - this.startY);

        // Clamp so the dialog stays within the viewport (keep it fully visible).
        const rect = this.dialog.getBoundingClientRect();
        const minX = this.originX - rect.left;
        const maxX = win.innerWidth - rect.right + this.originX;
        const minY = this.originY - rect.top;
        const maxY = win.innerHeight - rect.bottom + this.originY;

        nextX = Math.min(Math.max(nextX, minX), maxX);
        nextY = Math.min(Math.max(nextY, minY), maxY);

        this.dialog.style.transform = `translate(${nextX}px, ${nextY}px)`;
    };

    private readonly onPointerUp = (): void => {
        this.dragging = false;
        if (this.dialog) {
            this.dialog.style.willChange = '';
        }
        this.dialog = null;
        this.document.body.style.userSelect = '';
        this.document.removeEventListener('pointermove', this.onPointerMove, true);
        this.document.removeEventListener('pointerup', this.onPointerUp, true);
    };

    /**
     * Resets the dragged position once the modal is dismissed, so the same reused
     * `.modal-dialog` element reopens centered. Attaches at most one observer per modal.
     */
    private observeClose(dialog: HTMLElement): void {
        const modal = dialog.closest('.modal') as (HTMLElement & { __dragCloseObserved?: boolean }) | null;
        if (!modal || modal.__dragCloseObserved) {
            return;
        }
        modal.__dragCloseObserved = true;

        const isOpen = (): boolean =>
            (modal.classList.contains('show') || modal.classList.contains('in')) &&
            modal.style.display !== 'none';

        const observer = new MutationObserver(() => {
            if (!isOpen()) {
                dialog.style.transform = '';
            }
        });
        observer.observe(modal, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    private readTranslate(el: HTMLElement): { x: number; y: number } {
        const match = /translate\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*\)/.exec(el.style.transform);
        if (!match) {
            return { x: 0, y: 0 };
        }
        return { x: parseFloat(match[1]), y: parseFloat(match[2]) };
    }

    private injectCursorStyle(): void {
        const id = 'draggable-modal-style';
        if (this.document.getElementById(id)) {
            return;
        }
        const style = this.document.createElement('style');
        style.id = id;
        style.textContent = `.modal-header { cursor: move; }
.modal-header button, .modal-header a, .modal-header input, .modal-header .close { cursor: pointer; }`;
        this.document.head.appendChild(style);
    }
}
